import React, { useEffect, useState, useRef, useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON, LayersControl, useMap } from "react-leaflet";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HistoryIcon from '@mui/icons-material/History';
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import proj4 from "proj4";
import "./HistoricalData.css";
import Information from "./Information";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { MapHomeControl, MapLegendControl } from "../../../common/MapControls/MapControls";
import CommonTable from "../../../common/CommonTable";

// Same GeoServer workspace/endpoint used by the Forecast page's working WMS layers
// (test_lsi). The historical layer was pointed at the unrelated "climate_data"
// service, which is why the TIFF overlays never rendered here.
const GEOSERVER_WMS_URL = "https://drr-dss.rimes.int/landslide_trigrs?service?";
const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const INITIAL_VIEW_STATE = {
  longitude: 125.7275,
  latitude: -8.8742,
  zoom: 8,
};

proj4.defs([
  ["EPSG:32751", "+proj=utm +zone=51 +south +datum=WGS84 +units=m +no_defs"],
  ["EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs"],
]);

const getFormattedDateForWMS = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getYesterdayDate = () => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  return yesterday;
};

const HISTORICAL_DATA_START_DATE_OBJ = new Date("2025-06-09T00:00:00");

const MapBoundsUpdater = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds);
    }
  }, [map, bounds]);
  return null;
};

const WmsHistoricalDataLayer = ({ selectedLayerName }) => {
  const map = useMap();
  const wmsLayerRef = useRef(null);
  useEffect(() => {
    if (!map || !selectedLayerName) {
      if (wmsLayerRef.current && map.hasLayer(wmsLayerRef.current)) {
        map.removeLayer(wmsLayerRef.current);
        wmsLayerRef.current = null;
      }
      return;
    }
    const layerString = `test_lsi:${selectedLayerName}`;
    console.log("Adding WMS Layer with name:", layerString);
    const wmsBaseUrl = GEOSERVER_WMS_URL;
    if (wmsLayerRef.current && map.hasLayer(wmsLayerRef.current)) {
      map.removeLayer(wmsLayerRef.current);
      wmsLayerRef.current = null;
    }
    const newWmsLayer = L.tileLayer.wms(wmsBaseUrl, {
      layers: layerString,
      format: "image/png",
      transparent: true,
      version: "1.1.0",
      crs: L.CRS.EPSG3857,
      eventHandlers: {
        load: () => console.log(`WMS layer ${selectedLayerName} loaded!`),
        tileerror: (e) => console.error(`WMS Tile Error for ${selectedLayerName}:`, e),
      },
    });
    newWmsLayer.addTo(map);
    wmsLayerRef.current = newWmsLayer;
    return () => {
      if (wmsLayerRef.current && map.hasLayer(wmsLayerRef.current)) {
        map.removeLayer(wmsLayerRef.current);
      }
    };
  }, [map, selectedLayerName]);
  return null;
};

const fixedLandslideLayers = [
  // { value: "select-layer", label: "Select Fixed Layer", disabled: true },
  // { value: "landslide_forecast_2021-04-21", label: "Seroja April 21, 2021" },
  // { value: "landslide_forecast_2020-05-16", label: "May 2020 Heavy rainfalls 3" },
  // { value: "landslide_forecast_2020-03-11", label: "March 2020 Heavy rainfalls 2" },
  // { value: "landslide_forecast_2020-03-02", label: "March 2020 Heavy rainfalls 1" },
  // { value: "landslide_forecast_2019-12-28", label: "December 2019 Heavy rainfalls 3" },
  // { value: "landslide_forecast_2019-12-27", label: "December 2019 Heavy rainfalls 2" },
  // { value: "landslide_forecast_2019-12-23", label: "December 2019 Heavy rainfalls 1" },
  // { value: "landslide_forecast_2019-05-08", label: "2019 Cyclone Lili" },
  // { value: "2019-05-08_CycloneLili", label: "20199 Cyclone Lili" },
  // { value: "landslide_forecast_2019-03-08", label: "March 2019 Heavy rainfalls 2" },
  // { value: "landslide_forecast_2019-03-07", label: "March 2019 Heavy rainfalls 1" },
  // { value: "landslide_forecast_2013-06-22", label: "June –July 2013 Heavy rainfalls 3" },
  // { value: "landslide_forecast_2013-06-21", label: "June –July 2013 Heavy rainfalls 2" },
  // { value: "landslide_forecast_2013-06-07", label: "June –July 2013 Heavy rainfalls 1" },
  { value: "select-layer", label: "Select Fixed Layer", disabled: true },
  { value: "2013-06-07_JuneJulyRainfall", label: "June –July 2013 Heavy rainfalls 1" },
  { value: "2013-06-21_JuneJulyRainfall", label: "June –July 2013 Heavy rainfalls 2" },
  { value: "2013-06-22_JuneJulyRainfall", label: "June –July 2013 Heavy rainfalls 3" },
  { value: "2019-03-07_MarchRainfall", label: "March 2019 Heavy rainfalls 1" },
  { value: "2019-03-08_MarchRainfall", label: "March 2019 Heavy rainfalls 2" },
  { value: "2019-12-23_DecemberRainfall", label: "December 2019 Heavy rainfalls 1" },
  { value: "2019-12-27_DecemberRainfall", label: "December 2019 Heavy rainfalls 2" },
  { value: "2019-12-28_DecemberRainfall", label: "December 2019 Heavy rainfalls 3" },
  { value: "2019-05-08_CycloneLili", label: "2019 Cyclone Lili" },
  { value: "2020-03-02_MarchRainfall", label: "March 2020 Heavy rainfalls 1" },
  { value: "2020-03-11_MarchRainfall", label: "March 2020 Heavy rainfalls 2" },
  { value: "2020-05-16_MayRainfall", label: "May 2020 Heavy rainfalls 3" },
  { value: "2021-04-03_CycloneSeroja", label: "Seroja April 03, 2021" },
  { value: "2021-04-04_CycloneSeroja", label: "Seroja April 04, 2021" },
  { value: "2021-04-21_CycloneSeroja", label: "Seroja April 21, 2021" },
];

const getOnEachFeature = (dataRows) => (feature, layer) => {
  const code = feature.properties.ADM_Code || feature.properties.ADM3 || "";
  const row = dataRows.find(r =>
    r.Suco === feature.properties.ADM3 || r.Suco === code
  );
  if (row) {
    const forecastDate = row.forecastDate || 'N/A';

    const popupContent = `
      <div style="font-family: Arial,sans-serif; font-size: 13px; max-width:270px;">
        <h3 style="margin-bottom:4px;">Suco: <b>${row.Suco}</b></h3>
          <b>Municipality:</b> ${row.Municipality || ''}<br/>
          <b>Post Admin:</b> ${row.PostAdmin || ''}<br/>
          <b>Forecast Date:</b> ${forecastDate}<br/>
          <b>Advisory:</b>
                         <span style="
                padding: 2px 5px; 
                border-radius: 3px; 
                font-weight: bold; 
                color: black; 
                display: inline-block;
                text-decoration: underline;
            ">
             ${row.advisory || 'N/A'}
      </span><br/>
          <b>Total Population:</b> ${row.Population || 0}<br/>
          <b>Total Male:</b> ${row.Male || 0}<br/>
          <b>Total Female:</b> ${row.Female || 0}<br/>
          <b>HH Urban:</b> ${row.HH_Urban || 0}<br/>
          <b>HH Rural:</b> ${row.HH_Rural || 0}<br/>
          <b>Household:</b> ${row.Household || 0}
      </div>`;
    layer.bindPopup(popupContent);
  } else {
    layer.bindPopup("No data for this suco.");
  }
};

const HistoricalData = () => {
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [mapFooterDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [municipalityBoundary, setMunicipalityBoundary] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [filterMode, setFilterMode] = useState('dropdown');
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [, setMunicipalities] = useState([]);
  const [, setSubMunicipalities] = useState([]);
  const [, setSucos] = useState([]);

  const legendColors = useMemo(() => ({
    NoRisk: "rgb(148, 203, 85)",
    Low: "rgb(243, 235, 18)",
    Medium: "rgb(243, 155, 58)",
    High: "rgb(230, 45, 58)",
    "N/A": "rgb(222,222,223)",
    All: '#f0f0f0',
  }), []);

  const fetchData = async (date = null) => {
    setLoading(true);
    try {
      const dateQuery = date ? `filtered/?date=${getFormattedDateForWMS(date)}` : 'current/';
      const [statsResponse, munisResponse, subMunisResponse, sucosResponse, boundaryResponse] = await Promise.all([
        fetch(`${backendUrl}landslide/landslide-impact-risk-stats/${dateQuery}`),
        fetch(`${backendUrl}Municipalities/municipalities_list/`, { method: 'POST' }),
        fetch(`${backendUrl}Municipalities/sub_municipalities_list/`, { method: 'POST' }),
        fetch(`${backendUrl}Municipalities/suco_list/`, { method: 'POST' }),
        fetch("/DATA/OSMbased_adm3_boundary.json"),
      ]);
      const statsData = await statsResponse.json();
      const munisData = await munisResponse.json();
      const subMunisData = await subMunisResponse.json();
      const sucosData = await sucosResponse.json();
      const boundaryData = await boundaryResponse.json();

      setMunicipalities(munisData);
      setSubMunicipalities(subMunisData);
      setSucos(sucosData);
      setMunicipalityBoundary(boundaryData);

      const muniMap = new Map(munisData.map(m => [m.adm1_code, m.adm1]));
      const subMuniMap = new Map(subMunisData.map(s => [s.adm2_code, s.adm2]));
      const sucoMap = new Map(sucosData.map(s => [s.adm3_code, s.adm3]));
      const socioEconomicMap = new Map(
        boundaryData.features.map((feature) => [
          feature.properties.ADM_Code,
          {
            Population: feature.properties.Population || 0,
            Male: feature.properties.Male || 0,
            Female: feature.properties.Female || 0,
            HH_Urban: feature.properties.HH_Urban || 0,
            HH_Rural: feature.properties.HH_Rural || 0,
            Household: feature.properties.Household || 0,
          },
        ])
      );

      // The API can return rows spanning more than one date and can repeat the same
      // Suco more than once for a given date, so pin the result to a single target
      // date and drop duplicate adm_code rows before building the table.
      const targetDate = date
        ? getFormattedDateForWMS(date)
        : statsData.reduce((latest, item) => (!latest || item.date > latest ? item.date : latest), null);

      const dedupedStats = Array.from(
        new Map(
          statsData
            .filter((item) => item.date === targetDate)
            .map((item) => [item.adm_code, item])
        ).values()
      );

      const processedData = dedupedStats.map((item, index) => {
        const adm1Code = item.adm_code.substring(0, 4);
        const adm2Code = item.adm_code.substring(0, 6);
        const socioData = socioEconomicMap.get(item.adm_code) || {
          Population: 0,
          Male: 0,
          Female: 0,
          HH_Urban: 0,
          HH_Rural: 0,
          Household: 0,
        };
        return {
          SlNo: index + 1,
          Municipality: muniMap.get(adm1Code) || 'N/A',
          PostAdmin: subMuniMap.get(adm2Code) || 'N/A',
          Suco: sucoMap.get(item.adm_code) || item.adm_code,
          whi: item.whi,
          low_perc: item.low_perc,
          med_perc: item.med_perc,
          high_perc: item.high_perc,
          no_perc: item.no_perc,
          advisory: item.advisory,
          forecastDate: item.date,
          Population: socioData.Population,
          Male: socioData.Male,
          Female: socioData.Female,
          HH_Urban: socioData.HH_Urban,
          HH_Rural: socioData.HH_Rural,
          Household: socioData.Household,
        };
      });
      setTableData(processedData);

      if (boundaryData && boundaryData.features && boundaryData.features.length > 0) {
        const geoJsonLayer = L.geoJSON(boundaryData);
        setMapBounds(geoJsonLayer.getBounds());
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (filterMode === 'calendar' && selectedCalendarDate) {
      setLoading(true);
      fetchData(selectedCalendarDate).finally(() => setLoading(false));
    } else if (filterMode === 'dropdown') {
      setTableData([]);
      setSelectedLayer(null);
    }
  }, [selectedCalendarDate, filterMode]);

  const combinedFilteredData = useMemo(() => {
    return [...tableData].sort(
      (a, b) =>
        (a.Municipality || '').localeCompare(b.Municipality || '') ||
        (a.PostAdmin || '').localeCompare(b.PostAdmin || '') ||
        (a.Suco || '').localeCompare(b.Suco || '')
    );
  }, [tableData]);

  const columns = useMemo(() => [
    { key: 'SlNo', label: 'Sl No' },
    { key: 'Municipality', label: 'Municipality', sortable: true },
    { key: 'PostAdmin', label: 'Post Admin', sortable: true },
    { key: 'Suco', label: 'Suco', sortable: true },
    {
      key: 'advisory',
      label: 'Advisory',
      sortable: true,
      render: (row, getColor) => (
        <span style={{ backgroundColor: getColor(row.advisory), padding: '2px 5px', borderRadius: '3px', color: 'black', fontWeight: 'bold' }}>
          {row.advisory}
        </span>
      ),
    },
    { key: 'Population', label: 'Total Population', sortable: true, align: 'right' },
    { key: 'Male', label: 'Total Male', sortable: true, align: 'right' },
    { key: 'Female', label: 'Total Female', sortable: true, align: 'right' },
    { key: 'HH_Urban', label: 'HH Urban', sortable: true, align: 'right' },
    { key: 'HH_Rural', label: 'HH Rural', sortable: true, align: 'right' },
    { key: 'Household', label: 'Household', sortable: true, align: 'right' },
  ], []);

  // useEffect(() => {
  //   if (filterMode === 'dropdown' && selectedLayer && selectedLayer !== 'select-layer') {
  //     const layerInfo = fixedLandslideLayers.find(l => l.value === selectedLayer);
  //     setMapFooterDescription(layerInfo ? `Historical Landslide Data: ${layerInfo.label}` : "Select a layer to view historical landslide data.");
  //   } else if (filterMode === 'calendar' && selectedCalendarDate) {
  //     setMapFooterDescription(`Previous Forecast records: ${getFormattedDateForWMS(selectedCalendarDate)}`);
  //   } else {
  //     setMapFooterDescription("Select a layer or date to view historical landslide data.");
  //   }
  // }, [selectedLayer, selectedCalendarDate, filterMode]);

  useEffect(() => {
    const loadMunicipalityBoundary = async () => {
      try {
        const response = await fetch("/DATA/OSMbased_adm3_boundary.json");
        const data = await response.json();
        setMunicipalityBoundary(data);

        if (data && data.features && data.features.length > 0) {
          const geoJsonLayer = L.geoJSON(data);
          setMapBounds(geoJsonLayer.getBounds());
        }
      } catch (error) {
        console.error("Error loading municipality boundary:", error);
      }
    };
    loadMunicipalityBoundary();
  }, []);

  const handleFixedLayerChange = (event) => {
    setLoading(true);
    const value = event.target.value;
    if (value === 'select-layer') {
      setSelectedLayer(null);
    } else {
      setSelectedLayer(value);
      setSelectedCalendarDate(null);
    }
    setTimeout(() => setLoading(false), 700);
  };

  const handleCalendarDateChange = (date) => {
    setLoading(true);
    setSelectedCalendarDate(date);
    setSelectedLayer(null);
    if (document.getElementById('fixed-landslide-select')) {
      document.getElementById('fixed-landslide-select').value = 'select-layer';
    }
    setTimeout(() => setLoading(false), 700);
  };

  const toggleFilterMode = (mode) => {
    setFilterMode(mode);
    setSelectedLayer(null);
    setSelectedCalendarDate(null);
    if (document.getElementById('fixed-landslide-select')) {
      document.getElementById('fixed-landslide-select').value = 'select-layer';
    }
  };

  return (
    <div className="map-container-WindSpeedCyclone" style={{ backgroundColor: "#f2f3f4" }}>
      {/* Filter Bar - above map */}
      <Box
        sx={{
          p: 2.5,
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.03)",
          border: "1px solid #e8ecf1",
          marginBottom: "16px",
          overflow: "visible",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
          {/* Filter Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FilterAltIcon sx={{ color: "#0d6efd", fontSize: 22 }} />
            <span style={{ fontWeight: 700, fontSize: "15px", color: "#333" }}>Filter</span>
          </Box>

          {/* Divider */}
          <Box sx={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

          {/* Mode Toggle */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Historical Data" placement="bottom" arrow>
              <IconButton
                onClick={() => toggleFilterMode('dropdown')}
                size="small"
                sx={{
                  backgroundColor: filterMode === 'dropdown' ? '#0d6efd' : '#f5f5f5',
                  color: filterMode === 'dropdown' ? 'white' : '#333',
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: filterMode === 'dropdown' ? '#0b5ed7' : 'rgba(0,0,0,0.08)' },
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Previous Forecast records" placement="bottom" arrow>
              <IconButton
                onClick={() => toggleFilterMode('calendar')}
                size="small"
                sx={{
                  backgroundColor: filterMode === 'calendar' ? '#0d6efd' : '#f5f5f5',
                  color: filterMode === 'calendar' ? 'white' : '#333',
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: filterMode === 'calendar' ? '#0b5ed7' : 'rgba(0,0,0,0.08)' },
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <CalendarTodayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Divider */}
          <Box sx={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

          {/* Dropdown Mode */}
          {filterMode === 'dropdown' && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span style={{ fontWeight: 600, fontSize: "14px", color: "#333", whiteSpace: "nowrap" }}>Historical Data:</span>
              <select
                id="fixed-landslide-select"
                onChange={handleFixedLayerChange}
                value={selectedLayer || 'select-layer'}
                style={{ fontSize: "13px", padding: "6px 10px", borderRadius: "6px", border: "1px solid #ddd", minWidth: "260px" }}
              >
                {fixedLandslideLayers.map((layer) => (
                  <option key={layer.value} value={layer.value} disabled={layer.disabled}>
                    {layer.label}
                  </option>
                ))}
              </select>
            </Box>
          )}

          {/* Calendar Mode */}
          {filterMode === 'calendar' && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span style={{ fontWeight: 600, fontSize: "14px", color: "#333", whiteSpace: "nowrap" }}>Previous Forecast records:</span>
              <DatePicker
                selected={selectedCalendarDate}
                onChange={handleCalendarDateChange}
                dateFormat="yyyy-MM-dd"
                minDate={HISTORICAL_DATA_START_DATE_OBJ}
                maxDate={getYesterdayDate()}
                placeholderText="Select a date"
                className="dropdown"
              />
            </Box>
          )}
        </Box>
      </Box>

      <div className="map-wrapper">
        {loading && (
          <div className="map-loading-overlay">
            <div className="spinner"></div>
            <p>Loading map layers...</p>
          </div>
        )}
        <MapContainer
          center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
          zoom={INITIAL_VIEW_STATE.zoom}
          className="mapcontainerall" 
          style={{ zIndex: 0}}
        >
          <MapHomeControl />
          <Information />
          <MapBoundsUpdater bounds={mapBounds} />

          {/* Rendered as plain MapContainer children (not inside LayersControl.Overlay) to
              match the working Forecast page: WmsHistoricalDataLayer manages its own Leaflet
              layer via useMap()/addTo(map) rather than through the LayersControl's layer
              registration, so nesting it in an Overlay never actually surfaces the tiles. */}
          {selectedLayer && (
            <WmsHistoricalDataLayer selectedLayerName={`${selectedLayer}.tif`} />
          )}

          {filterMode === 'calendar' && selectedCalendarDate && !loading && (
            <WmsHistoricalDataLayer selectedLayerName={`landslide_forecast_${getFormattedDateForWMS(selectedCalendarDate)}`} />
          )}

          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <LayersControl position="topright">

            {municipalityBoundary && (
              <LayersControl.Overlay name="Suco" checked>
                <GeoJSON data={municipalityBoundary} style={{ color: "black", weight: 2, fillOpacity: 0 }} />
              </LayersControl.Overlay>
            )}

            {filterMode === 'calendar' && selectedCalendarDate && !loading && (
              <LayersControl.Overlay name="Suco Data Popup" checked>
                <GeoJSON
                  key={getFormattedDateForWMS(selectedCalendarDate)}
                  data={municipalityBoundary}
                  style={{ color: "black", weight: 2, fillOpacity: 0 }}
                  onEachFeature={getOnEachFeature(tableData)}
                />
              </LayersControl.Overlay>
            )}
          </LayersControl>
          <MapLegendControl
            position="bottomright"
            sections={[
              {
                shape: "square",
                items: [
                  { label: "High", color: "rgb(230, 45, 58)" },
                  { label: "Medium", color: "rgb(243, 155, 58)" },
                  { label: "Low", color: "rgb(243, 235, 18)" },
                  { label: "No-Risk", color: "rgb(148, 203, 85)" },
                ],
              },
            ]}
          />
          <div className="map-footer">
            <p>{mapFooterDescription}</p>
          </div>
        </MapContainer>
        {filterMode === 'calendar' && (
          <Box sx={{ p: 2, borderTop: '1px solid #ddd', width: '100%' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
              Past Forecast ({selectedCalendarDate ? getFormattedDateForWMS(selectedCalendarDate) : 'Select a date'})
            </Typography>
            <CommonTable
              tableData={combinedFilteredData}
              columns={columns}
              showFilter
              filterOptions={['All', 'NoRisk', 'Low', 'Medium', 'High']}
              legendColors={legendColors}
              searchableKeys={['Municipality', 'PostAdmin', 'Suco']}
              tableName="Historical_Landslide"
              currentTimestamp={selectedCalendarDate ? getFormattedDateForWMS(selectedCalendarDate) : undefined}
              emptyMessage="No impact data found."
            />
          </Box>
        )}
      </div>
    </div>
  );
};

export default HistoricalData;